import { useEffect } from "react";
import { isRouteErrorResponse, useRouteError } from "react-router";
import * as Sentry from "@sentry/react";

import {
  canReloadForFreshBuild,
  isChunkLoadError,
  reloadForFreshBuild,
} from "@/app/chunk-recovery";

/**
 * Root-route `errorElement`. Without one, `createBrowserRouter` renders its own
 * "Unexpected Application Error!" developer page — which is what a user sees
 * when a lazy page chunk 404s after a redeploy.
 *
 * - A stale-chunk error auto-reloads once to the fresh build (guarded so a
 *   genuinely-missing chunk can't loop); the user sees a brief "đang tải" state.
 * - Anything else shows a friendly card with a manual reload and is reported to
 *   Sentry.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const isChunk = isChunkLoadError(
    isRouteErrorResponse(error) ? error.statusText : error,
  );
  // Decide at render (pure) whether we'll auto-reload; the reload itself fires
  // in the effect so render stays side-effect-free.
  const willReload = isChunk && canReloadForFreshBuild();

  useEffect(() => {
    if (willReload) {
      reloadForFreshBuild();
    } else if (!isChunk) {
      Sentry.captureException(error);
    }
  }, [error, isChunk, willReload]);

  if (willReload) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 p-8 text-center">
        <div
          className="size-6 animate-spin rounded-full border-2 border-border border-t-foreground"
          aria-hidden="true"
        />
        <p className="text-sm text-muted-foreground">Đang tải phiên bản mới…</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 p-8 text-center">
      <h1 className="text-lg font-semibold">Đã có lỗi xảy ra</h1>
      <p className="max-w-sm text-sm text-muted-foreground">
        Vui lòng tải lại trang. Sự cố đã được ghi nhận.
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="mt-1 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium transition-colors hover:bg-muted"
      >
        Tải lại trang
      </button>
    </div>
  );
}
